'use client';

import { useState } from 'react';
import { MessageSquare, MessageCircle, Phone, Mail, Send, Check, User, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface GetInTouchModalProps {
  isOpen: boolean;
  onClose: () => void;
  source?: string;
}

export default function GetInTouchModal({ isOpen, onClose, source = 'unknown' }: GetInTouchModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    lookingFor: 'myself',
    message: "Hi! I'm interested in properties at Spire Residences. Please provide more details."
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const contactMethods = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#1FAD4F]'
    },
    {
      id: 'viber',
      label: 'Viber',
      icon: Phone,
      color: 'bg-[#7360F2] hover:bg-[#5A4DC2]'
    },
    {
      id: 'facebook',
      label: 'Facebook Messenger',
      icon: MessageCircle,
      color: 'bg-[#1877F2] hover:bg-[#145DBF]'
    },
    {
      id: 'inquiry',
      label: 'Send Inquiry',
      icon: Mail,
      color: 'bg-[#d60500] hover:bg-[#b50400]'
    },
  ];

  const handleContactMethod = (method: string) => {
    setSelectedMethod(method);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let preferredContact = 'WhatsApp';
      if (selectedMethod === 'viber') {
        preferredContact = 'Viber';
      } else if (selectedMethod === 'facebook') {
        preferredContact = 'Facebook Messenger';
      }

      const response = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: null,
          propertyTitle: 'General Inquiry - Spire Residences',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          lookingFor: formData.lookingFor,
          preferredContact: preferredContact,
          inquiryType: 'General Inquiry',
          message: formData.message,
          source: source
        })
      });

      if (response.ok) {
        setShowThankYou(true);

        setTimeout(() => {
          const lookingForLabel = formData.lookingFor === 'myself' ? 'Looking for Myself' : 'I am Broker';
          const baseMessage = `New Inquiry - Spire Residences\n\nContact:\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nType: ${lookingForLabel}\nPreferred: ${preferredContact}\n\nMessage:\n${formData.message}`;

          if (selectedMethod === 'whatsapp') {
            window.open(`https://wa.me/639602535917?text=${encodeURIComponent(baseMessage)}`, '_blank');
          } else if (selectedMethod === 'viber') {
            window.open(`viber://chat?number=%2B639602535917`, '_blank');
          } else if (selectedMethod === 'facebook') {
            window.open(`https://m.me/spireresidences`, '_blank');
          } else {
            window.open(`https://wa.me/639602535917?text=${encodeURIComponent(baseMessage)}`, '_blank');
          }

          setTimeout(() => {
            setSelectedMethod(null);
            setShowThankYou(false);
            setFormData({
              name: '',
              email: '',
              phone: '',
              lookingFor: 'myself',
              message: "Hi! I'm interested in properties at Spire Residences. Please provide more details."
            });
            onClose();
          }, 2000);
        }, 1500);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Contact Methods Modal */}
      {!selectedMethod && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={onClose}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 font-heading">Choose Contact Method</h3>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-6 text-sm md:text-base">Select your preferred way to get in touch</p>
            <div className="grid grid-cols-1 gap-3">
              {contactMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => handleContactMethod(method.id)}
                  className={`${method.color} text-white p-3 md:p-4 rounded-xl transition-all hover:scale-[1.02] active:scale-95 shadow-md hover:shadow-lg flex items-center justify-between group`}
                >
                  <div className="flex items-center gap-2 md:gap-3">
                    <method.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-semibold text-sm md:text-base">{method.label}</span>
                  </div>
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Inquiry Form Modal */}
      {selectedMethod && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={() => setSelectedMethod(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-8" onClick={(e) => e.stopPropagation()}>
            {showThankYou ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-[#d60500]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
                <p className="text-gray-600">We'll contact you via your preferred method shortly.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-[#d60500] to-[#b50400]">
                  <h2 className="text-2xl font-bold text-white">Send Inquiry</h2>
                  <button onClick={() => setSelectedMethod(null)} className="p-2 hover:bg-white/20 rounded-full">
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent" placeholder="Juan Dela Cruz" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">I am *</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer flex-1">
                        <input type="radio" name="lookingFor" value="myself" checked={formData.lookingFor === 'myself'} onChange={(e) => setFormData({ ...formData, lookingFor: e.target.value })} className="w-4 h-4 text-[#d60500]" />
                        <span className="text-sm">Looking for Myself</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer flex-1">
                        <input type="radio" name="lookingFor" value="broker" checked={formData.lookingFor === 'broker'} onChange={(e) => setFormData({ ...formData, lookingFor: e.target.value })} className="w-4 h-4 text-[#d60500]" />
                        <span className="text-sm">I am Broker</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#d60500]" placeholder="juan@example.com" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#d60500]" placeholder="+63 912 345 6789" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                    <textarea required value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={4} className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#d60500] resize-none" />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#d60500] hover:bg-[#b50400] text-white h-12">
                    {isSubmitting ? 'Processing...' : <><Send className="w-5 h-5 mr-2" /> Send Inquiry</>}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">By submitting, you agree to be contacted via your preferred method.</p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
